import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { api, clearToken, getToken, setToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wishlist, setWishlist] = useState([]); // array of tour_ids

  const refreshWishlist = useCallback(async () => {
    try {
      const { data } = await api.get("/wishlist");
      setWishlist(data.map((d) => d.tour_id));
    } catch {
      setWishlist([]);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      const { data: wl } = await api.get("/wishlist");
      setWishlist(wl.map((d) => d.tour_id));
    } catch {
      setUser(null);
      setWishlist([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  }, []);

  const finaliseSession = useCallback(async (session_id) => {
    const { data } = await api.post("/auth/session", { session_id });
    if (data.session_token) setToken(data.session_token);
    setUser(data.user);
    await refreshWishlist();
    return data.user;
  }, [refreshWishlist]);

  const logout = useCallback(async () => {
    try { await api.post("/auth/logout"); } catch {}
    clearToken();
    setUser(null);
    setWishlist([]);
  }, []);

  const toggleWishlist = useCallback(async (tour_id) => {
    if (!user) return { ok: false, reason: "not-authed" };
    if (wishlist.includes(tour_id)) {
      await api.delete(`/wishlist/${tour_id}`);
      setWishlist((w) => w.filter((x) => x !== tour_id));
      return { ok: true, added: false };
    } else {
      await api.post("/wishlist", { tour_id });
      setWishlist((w) => [...w, tour_id]);
      return { ok: true, added: true };
    }
  }, [user, wishlist]);

  const value = {
    user,
    loading,
    wishlist,
    login,
    logout,
    finaliseSession,
    toggleWishlist,
    refreshWishlist,
    isInWishlist: (id) => wishlist.includes(id),
    hasToken: !!getToken(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
}
